import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import ColorPicker from "../../common/components/ColorPicker";
import { Col, Row } from "../../common/components/Grid";
import FormEditor from "../../common/components/GridEditor";
import NumericInput from "../../common/components/NumericInput";
import ReactForm from "../../common/components/ReactForm";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { canCreateLevel, canRemoveLevel, canUpdateLevel, createLevel, removeLevel, updateLevel, useLevels } from "../../reducers/levelSlice";
import GroupedSettings from "./GroupedSettings";
import PropTypes from 'prop-types';
import CustomForm from "../../common/components/CustomForm";

const defaultSort = ['sequence','name'];

const newLevelTemplate = { customfields: [], sequence: 1 };

const LevelDetails = forwardRef(({ value: level, groupCtrl, disabled = false, onChange }, formRef) => {
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    return <>
        <ReactForm ref={formRef}>
            <Row>
                <Col md={4}>
                    <TextInput placeholder={t('level_one')} readonly={level.internalid} value={level.name} onChange={value => onChange('name')(value)} disabled={disabled} required></TextInput>
                </Col>
                <Col md={4}>
                    <ColorPicker placeholder={t('color')} value={level.color} onChange={value => onChange('color')(value)} disabled={disabled} required></ColorPicker>
                </Col>
                <Col md={4}>
                    <NumericInput placeholder={t('sequence')} value={level.sequence} min={1} onChange={value => onChange('sequence')(value)} disabled={disabled} required></NumericInput>
                </Col>
            </Row>
            { clientConfig.levelCustomFields && <CustomForm def={clientConfig.levelCustomFields} data={level.customfields} onChange={value => onChange('customfields')(value)} required></CustomForm> }
            {groupCtrl}
        </ReactForm>
        <Row>
            <Col>
                <Card title={t('customfields')}>
                    <FormEditor def={level.formdef} disabled={disabled} onChange={value => onChange('formdef')(value)}></FormEditor>
                </Card>
            </Col>
        </Row>
        { clientConfig.enableCrews && <Row>
            <Col>
                <Card title={t('crew_customfields')}>
                    <FormEditor def={level.crew_formdef} disabled={disabled} onChange={crew_formdef => onChange('crew_formdef')(crew_formdef)} hideAccess></FormEditor>
                </Card>
            </Col>
        </Row> }
    </>
});

const Levels = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const levels = useLevels();
    const sortFields = useMemo(() => ({
        name: t('name'),
        sequence: t('sequence')
    }), [t]);
    const groupingCtrls = useMemo(() => {
        const ctrls = [];
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site'], sortfield: 'site', required: true });
        }
        return ctrls;
    }, [t, clientConfig.enableSites]);
    const onAdd = useCallback(level => {
        delete level.internalid;
        return createLevel(network, level);
    }, [network]);
    const onUpdate = useCallback(delta => {
        const origLevel = levels.find(l => l.internalid === delta.internalid)
        return updateLevel(network, { internalid: delta.internalid, site: origLevel.site, name: origLevel.name, ...delta });
    }, [network, levels]);
    const onRemove = useCallback(level => {
        return removeLevel(network, level);
    }, [network]);
    return <GroupedSettings Details={LevelDetails} data={levels} title={label} uuid={uuid}
            defaultEntry={newLevelTemplate} defaultSort={defaultSort} sortFields={sortFields}
            onClose={onClose} makeLabel={e => {
                return <Row>
                    <Col className="px-2" xs="auto">
                        <div className="px-2 py-1" style={{ backgroundColor: e.color }}>&nbsp;</div>
                    </Col>
                    <Col>{e.name}</Col>
                </Row>
            }}
            initialGroupingField="type"
            groupingExclusions={['site','level','internalid']}
            groupingCtrls={groupingCtrls}
            canAdd={entry => canCreateLevel(credentials, entry)} canUpdate={entry => canUpdateLevel(credentials, entry)} canRemove={entry => canRemoveLevel(credentials, entry)}
            onAdd={onAdd} onUpdate={onUpdate} onRemove={onRemove}></GroupedSettings>
}
Levels.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default Levels;